import { useState, useEffect, useReducer } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { GlobalContext, storeReducer } from "../lib/auth/globalStore";
import PrivateRoute from "./PrivateRoute";
import Login from "./Login";
import { Integrations } from "@sentry/tracing";
import * as Sentry from "@sentry/react";
import PointOfSale from "./PointOfSale";
import Sync from "./Sync";
import OpenTill from "./OpenTill";
import CloseTill from "./CloseTill";
import CloseTillAll from "./CloseTill/CloseTIllAll";
import CashToKeep from "./CloseTill/cashToKeep";
import OpenTillComponent from "./OpenTill/OpenTillComponent";
import TableManagement from "./PointOfSale/Restaurant/Tables-Management/tables";
import ProductionOrder from "./ProductionOrder";
import PurchaseOrder from "./PurchaseOrder";
import ProductionRMIssue from "./ProductionRMIssue";
import Inventory from "./Inventory";
import Aggregator from "./Aggregator";
import CallCenter from "./CallCenter";
import Estimate from "./Estimate";
import Preorder from "./PreOrder";
import Till from "./Till/Till";
import Dashboard2 from "./Dashboard2";
import "./style.css";
import ErrorBoundary from "./ErrorBoundary";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { EventBusProvider } from "../lib/hooks/EventBusProvider";
import { socket } from "../socket";
import SignIn from "./Authentication";
import OrderNotification from "./OrderNotification";
import { getSalesHistoryDetails } from "../utility/salesHistoryUtils";
import NavigationBlocker from "../NavigationBlocker";
import DualScreenPOS from "./dualScreen";
import { MultiTabDetector } from "./MultiTabDetector";
import TillModalHandler from "./TillModalHandler";

const App = () => {
  const SentryDSN = process.env.REACT_APP_SENTRY_DSN;
  const SentryEnabled = process.env.REACT_APP_ENABLE_SENTRY;
  const environment = process.env.REACT_APP_Sentry_Environment;
  const [orderQueue, setOrderQueue] = useState([]);
  const [visibleOrders, setVisibleOrders] = useState([]);
  const MAX_VISIBLE_NOTIFICATIONS = 1;
  const [orderUpdateFlag, setOrderUpdateFlag] = useState(0);

  const setTokens = () => {
    const getQueryParams = (location) => {
      const searchParams = new URLSearchParams(location.search);
      const params = {};
      for (const [key, value] of searchParams) {
        params[key] = value;
      }
      return params;
    };
    const location = window.location;
    const params = getQueryParams(location);
    if (params?.searchKey) {
      localStorage.setItem("tiilId", params?.searchKey);
      localStorage.setItem("tillName", params?.nameTill);
    }
  };

  useEffect(() => {
    let userPreferences = JSON.parse(localStorage.getItem("userPreferences"));
    if (!userPreferences?.dateFormat) {
      userPreferences = {
        dateFormat: "DD-MM-YYYY",
        dateTimeFormat: "DD-MM-YYYY HH:mm:ss",
      };
      localStorage.setItem("userPreferences", JSON.stringify(userPreferences));
    }
    setTokens();
  }, []);

  useEffect(() => {
    if (SentryEnabled) {
      Sentry.init({
        environment: environment,
        dsn: SentryDSN,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          new Integrations.BrowserTracing(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: 1.0,
        ignoreErrors: [
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications",
          "ResizeObserver loop completed with undelivered notifications.",
        ],
      });
    }
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on("order:new", (data) => {
        console.log("Received order:new:", data);
        setOrderQueue((prevQueue) => [...prevQueue, { data }]);
        setOrderUpdateFlag((prev) => prev + 1);
      });

      socket.on("order:update", async (data) => {
        setOrderUpdateFlag((prev) => prev + 1);
        if (data.new_state === "Acknowledged") {
          try {
            const orderDetails = await getSalesHistoryDetails(data.additional_info?.external_channel?.order_id, "AK", true, data?.order_id);
            if (!orderDetails) {
              console.error(`App.js No order details for sOrderID: ${data.additional_info?.external_channel?.order_id}`);
            } else if (orderDetails) {
              setVisibleOrders((prevOrders) => prevOrders?.filter((order) => order?.data?.externalId !== data?.additional_info?.external_channel?.order_id));
            }
          } catch (error) {
            console.error("App.js Error in socket order:update:", error);
          }
        }
      });
      socket.on("delivery:update", (data) => {
        console.log("Received delivery:update:", data);
        setOrderUpdateFlag((prev) => prev + 1);
      });

      return () => {
        socket.off("order:new");
        socket.off("order:update");
        socket.off("delivery:update");
      };
    }
  }, []);

  useEffect(() => {
    if (visibleOrders.length < MAX_VISIBLE_NOTIFICATIONS && orderQueue.length > 0) {
      const nextOrder = orderQueue[0];
      setVisibleOrders((prev) => [...prev, nextOrder]);
      setOrderQueue((prev) => prev.slice(1));
    }
  }, [orderQueue, visibleOrders]);

  let percent = "0%";
  let productCategoryPercentage = "0";
  let productPercentage = "0";
  let promotionPercentage = "0";
  const [globalStore, setGlobalStore] = useReducer(storeReducer, {
    percent: percent,
    productCategoryPercentage: productCategoryPercentage,
    productPercentage: productPercentage,
    promotionPercentage: promotionPercentage,
  });
  const handleAutoAccept = async (orderData, { isManualAccept }) => {
    console.log("Order auto-accepted:", orderData);
    if (isManualAccept && orderData?.data?.orderId) {
      await getSalesHistoryDetails(orderData?.data?.orderId, "AK", true);
    }
    setVisibleOrders((prevOrders) => prevOrders.filter((order) => order.data.orderId !== orderData.data.orderId));
  };

  const handleReject = (orderData) => {
    console.log("Order rejected:", orderData);
    setVisibleOrders((prevOrders) => prevOrders.filter((order) => order.data.orderId !== orderData.data.orderId));
  };

  const handleOnClose = (orderData) => {
    setVisibleOrders((prevOrders) => prevOrders.filter((order) => order.data.orderId !== orderData.data.orderId));
  };

  return (
    <div className="aspectRatio">
      <MultiTabDetector onTabTakeOver={() => console.log("Tab taken over")} onTabClose={() => console.log("Tab closed")} />
      <GlobalContext.Provider value={{ globalStore, setGlobalStore }}>
        <I18nextProvider i18n={i18n}>
          <EventBusProvider>
            <Router>
              <ErrorBoundary>
                <NavigationBlocker />
                <TillModalHandler />
                <Switch>
                  <Route exact path="/" component={Till} />
                  <Route exact path="/login" component={Login} />
                  <Route exact path="/sign-in" component={SignIn} />
                  <PrivateRoute exact path="/pos" component={PointOfSale} />
                  <PrivateRoute exact path="/dualscreen" component={DualScreenPOS} />
                  <PrivateRoute exact path="/sync" component={Sync} />
                  <PrivateRoute exact path="/dashboard2" component={Dashboard2} />
                  <PrivateRoute exact path="/production" component={ProductionOrder} />
                  <PrivateRoute exact path="/production-rm-issue" component={ProductionRMIssue} />
                  <PrivateRoute exact path="/purchaseOrder" component={PurchaseOrder} />
                  <PrivateRoute exact path="/inventory/stock-indent" component={Inventory} />
                  <PrivateRoute exact path="/aggregator" component={Aggregator} orderUpdateFlag={orderUpdateFlag} />
                  <PrivateRoute exact path="/estimate" component={Estimate} />
                  <PrivateRoute exact path="/preorder" component={Preorder} />
                  <PrivateRoute exact path="/open-till" component={OpenTill} />
                  <PrivateRoute exact path="/cash-to-keep" component={CashToKeep} />
                  <PrivateRoute exact path="/open-till-component" component={OpenTillComponent} />
                  <PrivateRoute exact path="/close-till" component={CloseTill} />
                  <PrivateRoute exact path="/close-till-all" component={CloseTillAll} />
                  <PrivateRoute exact path="/table-management" component={TableManagement} />
                  <PrivateRoute exact path="/call-center" component={CallCenter} />
                </Switch>
              </ErrorBoundary>
            </Router>
          </EventBusProvider>
        </I18nextProvider>
      </GlobalContext.Provider>
      {visibleOrders.map((order, index) =>
        window.location.pathname !== "/aggregator" ? (
          <OrderNotification
            key={order.data.orderId}
            orderData={order}
            index={index}
            onAutoAccept={(options) => handleAutoAccept(order, options)}
            onReject={() => handleReject(order)}
            onClose={() => handleOnClose(order)}
          />
        ) : null
      )}
    </div>
  );
};

export default App;
